import { getCustomerAuthFromCookies } from '@bulknutrients/bulk-shared-lib/lib/api/auth';
import { getCustomerId } from '@bulknutrients/bulk-shared-lib/lib/api/customer';
import * as schema from '@bulknutrients/bulk-shared-lib/lib/drizzle/customer_data/schema';
import { drizzle } from '@bulknutrients/bulk-shared-lib/lib/drizzle/instances';
import {
  ambassadorCodeValid,
  ambassadorExistingCustomer,
} from '@bulknutrients/bulk-shared-lib/lib/postgres/ambassador';
import { Aside } from '@components/Aside/Aside';
import { Button } from '@components/Button';
import { Container } from '@components/Container';
import { CreateAccountForm } from '@components/CreateAccountForm/CreateAccountForm';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { StandardLayoutNoIndex } from '@components/Layouts/StandardLayout';
import { LoginModalUncontrolled } from '@components/LoginForm/LoginModal';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useCustomer } from '@hooks/customer/useCustomer';
import { log } from '@lib/logs';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { Pool } from 'pg';
import { type ReactElement, useEffect, useState } from 'react';

interface InvalidCodeProps {
  validCode: false;
  code: string;
  customerSignedIn?: boolean;
}

interface ValidNotSignedInProps {
  validCode: true;
  customerSignedIn: false;
  code: string;
  ambassadorName: string;
}

interface ValidSignedInProps {
  validCode: true;
  customerSignedIn: true;
  code: string;
  ambassadorName: string;
  // If the customer is already referred by an ambassador
  ambassadorReferredByName: string | null;
  ambassadorReferredByIsSame: boolean;
  referredByCode: string | null;
}

export default function AmbassadorReferralSignupPage(
  props: InvalidCodeProps | ValidNotSignedInProps | ValidSignedInProps
): ReactElement {
  const router = useRouter();
  const { customer } = useCustomer();

  const [ambassadorAttachErrors, setAmbassadorAttachErrors] = useState<
    string[]
  >([]);

  // For unsigned customers, reload the page if the customer signs in
  useEffect(() => {
    let mounted = true;

    if (customer && !props?.customerSignedIn && mounted) {
      setTimeout(() => {
        router.reload();
      }, 1500);
    }
    return () => {
      mounted = false;
    };
  }, [customer, props?.customerSignedIn, router]);

  const attachCustomerToAmbassador = async (ambassadorCode: string) => {
    const response = await fetch('/api/customer/ambassador-program/referred', {
      method: 'POST',
      body: JSON.stringify({ ambassadorCode }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 200) {
      router.reload();
    } else {
      setAmbassadorAttachErrors([
        'There was an error attaching you to the ambassador. Please try again.',
      ]);
    }
  };

  const { validCode, code, customerSignedIn } = props;

  // If the code is invalid, default to the first case
  if (validCode === false) {
    return (
      <Container
        as="main"
        id="main-content"
        className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        {/* <article className="mx-auto my-0 max-w-narrow-page px-2.5 py-6"> */}
        <h1>Sign Up through Ambassador Referral</h1>
        <Paragraph className="font-semibold">
          The code you provided ({code}) is not valid.
        </Paragraph>
        <Paragraph>
          Confirm the link from the ambassador referring you and try again to
          receive your reward.
        </Paragraph>
        {!customerSignedIn && (
          <>
            <Paragraph>
              Alternatively, you can sign up without a referral code.
            </Paragraph>
            <HorizontalDivider />
            <CreateAccountForm />
          </>
        )}
      </Container>
    );
  }

  const { ambassadorName } = props;
  // We know the customer is not signed in, so just go straight to the form with the ambassador name
  if (customerSignedIn === false) {
    return (
      <Container
        as="main"
        id="main-content"
        className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        <h1>Sign Up through Ambassador Referral</h1>
        <Paragraph>Great work!</Paragraph>
        <Paragraph>
          We've been able to validate the ambassador code you provided... looks
          like you've been referred by <b>{ambassadorName}</b>!
        </Paragraph>
        <Aside>
          For existing customers,{' '}
          <LoginModalUncontrolled
            trigger={
              <Button
                buttonStyle="text"
                className="inline"
                title="Sign in to be referred by an Ambassador"
              >
                please login
              </Button>
            }
            title={<>Login to your account</>}
          />{' '}
          to your Bulk Nutrients account first.
        </Aside>
        <Paragraph>
          Sign up below and you'll be emailed the 5% off coupon to use on your
          first purchase with Bulk Nutrients.
        </Paragraph>
        <HorizontalDivider />
        <CreateAccountForm ambassadorReferreredByCode={code} />
      </Container>
    );
  }

  const {
    ambassadorReferredByName,
    ambassadorReferredByIsSame,
    referredByCode,
  } = props;

  // Customer signed in but not yet referred by Ambassador
  if (!ambassadorReferredByName && !referredByCode) {
    return (
      <Container
        as="main"
        id="main-content"
        className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        <h1>Sign Up through Ambassador Referral</h1>

        <Paragraph>
          We've been able to validate the ambassador code you provided... looks
          like you've been referred by <b>{ambassadorName}</b>!
        </Paragraph>
        <Paragraph>
          As you already have a Bulk Nutrients account, we just require your
          confirmation below and then we'll email you a 5% off coupon!
        </Paragraph>
        <Paragraph>
          Once you've confirmed this, you won't be able to be referred by any
          other ambassadors.
        </Paragraph>

        <Button type="button" onClick={() => attachCustomerToAmbassador(code)}>
          Yes! I was referred by {ambassadorName}
        </Button>
        {ambassadorAttachErrors.length > 0 && (
          <Paragraph className="text-red-500">
            {ambassadorAttachErrors[0]}
          </Paragraph>
        )}
      </Container>
    );
  }
  // Customer signed in AND referred by ambassador AND was different ambassador
  if (!ambassadorReferredByIsSame) {
    return (
      <Container
        as="main"
        id="main-content"
        className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        <h1>Sign Up through Ambassador Referral</h1>

        <Paragraph>
          We've been able to validate the ambassador code you provided... looks
          like you've been referred by <b>{ambassadorName}</b>!
        </Paragraph>
        <Paragraph>
          We can see you're trying to indicate to us you were referred to Bulk
          Nutrients by {ambassadorName} but our records show you were
          {referredByCode
            ? ' already referred through our customer-based referral system.'
            : ` originally referred by ${ambassadorReferredByName}.`}
        </Paragraph>
        <Paragraph>
          You're not permitted to switch who you were originally referred by,
          but please{' '}
          <TextLink href="/contact-us">
            reach out to our Customer Service team{' '}
          </TextLink>
          if you believe this was in error.
        </Paragraph>
        <HorizontalDivider />
      </Container>
    );
  } else if (ambassadorReferredByIsSame) {
    return (
      <Container
        as="main"
        id="main-content"
        className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        <h1>Sign Up through Ambassador Referral</h1>

        <Paragraph>
          We've validated the ambassador code you provided... looks like you've
          been referred by <b>{ambassadorName}</b>
        </Paragraph>
        <Paragraph>
          It looks like you are currently referred by <b>{ambassadorName}</b>!
          As that is the case,{' '}
          <b>please check your email for a 5% off coupon code</b> and remember
          to apply it on checkout!
        </Paragraph>
        <Paragraph>
          Please <TextLink href="/contact-us">get in touch </TextLink>with our
          Customer Service team if you need any help.
        </Paragraph>
        <HorizontalDivider />
      </Container>
    );
  }
  return (
    <Container
      as="main"
      id="main-content"
      className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
    >
      <h1>Sign Up through Ambassador Referral</h1>
      <Paragraph>
        We've encountered an issue with validating your referral code and
        verifying it against your account.
      </Paragraph>
      <Paragraph>
        Confirm the link from the ambassador referring you and try again to
        receive your reward.
      </Paragraph>
      <Paragraph>
        Please{' '}
        <TextLink href="/contact-us">
          reach out to our Customer Service team{' '}
        </TextLink>
        if you believe this is an error.
      </Paragraph>
    </Container>
  );
}

AmbassadorReferralSignupPage.getLayout = function getLayout(
  page: ReactElement
) {
  return <StandardLayoutNoIndex>{page}</StandardLayoutNoIndex>;
};

export const getServerSideProps: GetServerSideProps = async ({
  params,
  req,
}) => {
  const ambassadorCode = params?.ambassadorCode;

  // Some people had issues with undefined ambassador codes so let's log it
  log({
    message: `Ambassador code: ${ambassadorCode}`,
    cookies: req.cookies,
    location: 'Ambassador Referral Signup Page / getServerSideProps',
  });

  if (!ambassadorCode) {
    return {
      props: {
        validCode: false,
        code: '',
      },
    };
  }

  const ambassadorCodeSingle = ambassadorCode as string;

  if (!ambassadorCodeSingle) {
    return {
      props: {
        validCode: false,
        code: '',
      },
    };
  }

  const isDevOrTestEnvironment =
    process.env.NEXT_PUBLIC_ENVIRONMENT === 'development' ||
    process.env.NEXT_PUBLIC_ENVIRONMENT === 'preview' ||
    process.env.TEST === 'true';

  const METADATA_DATABASE_TO_USE = isDevOrTestEnvironment
    ? process.env.CUSTOMER_METADATA_DATABASE_TEST
    : process.env.CUSTOMER_METADATA_DB;

  const pool = new Pool({
    user:
      process.env.CUSTOMER_METADATA_USER ??
      (() => {
        throw new Error('CUSTOMER_METADATA_USER is not set');
      })(),
    password:
      process.env.CUSTOMER_METADATA_PASSWORD ??
      (() => {
        throw new Error('CUSTOMER_METADATA_PASSWORD is not set');
      })(),
    database:
      METADATA_DATABASE_TO_USE ??
      (() => {
        throw new Error('CUSTOMER_METADATA_DB is not set');
      })(),
    host:
      process.env.CUSTOMER_METADATA_HOST ??
      (() => {
        throw new Error('CUSTOMER_METADATA_HOST is not set');
      })(),
    port: 5432,
  });

  const db = drizzle(pool, { schema });

  const ambassadorData = await ambassadorCodeValid(
    ambassadorCodeSingle,
    db,
    req.cookies
  );

  // Check if user already logged in
  const { cookies } = req;
  const customerAuth = getCustomerAuthFromCookies(cookies);
  const customerId = customerAuth && (await getCustomerId(customerAuth));

  if (!ambassadorData || ambassadorData.length === 0) {
    log({
      message: `No ambassador found for code: ${ambassadorCodeSingle}`,
      cookies,
      location: 'Ambassador Referral Signup Page / getServerSideProps',
    });
    return {
      props: {
        validCode: false,
        code: ambassadorCodeSingle,
        customerSignedIn: !!customerId,
      },
    };
  }

  if (!customerId) {
    log({
      message: 'Customer is not logged in',
      cookies,
      location: 'Ambassador Referral Signup Page / getServerSideProps',
    });
    return {
      props: {
        validCode: true,
        code: ambassadorCodeSingle,
        customerSignedIn: false,
        ambassadorName: ambassadorData[0].ambassador_display_name,
      },
    };
  }

  let ambassadorReferredBy: number | null = null;
  let ambassadorReferredByName: string | null = null;
  let referredByCode: string | null = null;

  log({
    message: 'Customer is logged in, check if referred by ambassador',
    cookies,
    location: 'Ambassador Referral Signup Page / getServerSideProps',
  });
  const currentCustomerMetadata = await ambassadorExistingCustomer(
    customerId,
    db,
    cookies
  );

  if (currentCustomerMetadata) {
    ambassadorReferredBy = currentCustomerMetadata[0].referring_ambassador_id;
    ambassadorReferredByName =
      currentCustomerMetadata[0].referring_ambassador_display_name;

    referredByCode = currentCustomerMetadata[0].referred_by_code;
  }

  let ambassadorReferredByIsSame = false;

  if (ambassadorReferredBy == ambassadorData[0].ambassador_customer_id) {
    ambassadorReferredByIsSame = true;
  }

  return {
    props: {
      customerSignedIn: !!customerId,
      ambassadorReferredByIsSame: ambassadorReferredByIsSame,
      ambassadorReferredByName: ambassadorReferredByName,
      validCode: true,
      code: ambassadorCodeSingle,
      ambassadorName: ambassadorData[0].ambassador_display_name,
      referredByCode: referredByCode,
    },
  };
};
